import { Injectable, EventEmitter, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { MessageService } from 'primeng/api';
import { AppProperty } from '../properties/app.property';
import { CompanyProperty } from '../properties/company.property';
import { AppService } from '../services/app.service';
import { DOCUMENT } from '@angular/common';
import { UserProperty } from '../properties/user.property';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators'
import { Observable } from 'rxjs';

import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment';
import { apps } from '../constants/app-constant';
import { OAuthProviderStatus, OAuthAuthorizeResponse } from '../models/oauth.class';
import { DebugLoggerService } from '../services/debug-logger.service';


@Injectable()
export class AppStateHolder {
  private _companyProperties = inject(CompanyProperty);
  private _appProperty = inject(AppProperty);
  private _appService = inject(AppService);
  private _cookieService = inject(CookieService);
  private http = inject(HttpClient);
  private _messageService = inject(MessageService);
  private _userProperty = inject(UserProperty);
  private _document = inject(DOCUMENT);
  private _router = inject(Router);
  private _debugLogger = inject(DebugLoggerService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() { }

  cId: any;
  companies: any;
  types: any[];
  holdingApps = [];
  cmpApp: any;
  activityParams = [];
  projectParams = [];
  stateGraphParams = [];
  hourParams = [];
  rtypes = [];
  typologyGraphParams = [];

  showAuthLoginModal: boolean = false;

  activityId: string;
  appSelected: EventEmitter<any> = new EventEmitter();
  appNotFound: EventEmitter<string> = new EventEmitter();

  // OAuth state
  oauthState = {
    providers: [] as OAuthProviderStatus[],
    authorizeResponse: null as OAuthAuthorizeResponse
  };


  loginWindowIsOpen: boolean = false;
  loginWindow: any;

  //#region App

  /**
   * Get app model from id
   * @param id
   * @returns Set app into appSelected
   */
  getApp(id: string): void {
    this._debugLogger.logAction('AppStateHolder', 'getApp', { id });
    let app = this._companyProperties.userCompanyApps.find(a => a.id == id.toLowerCase());
    if (app !== undefined) {
      if (document.location.host.indexOf("demo.") >= 0 && app.url.indexOf("demo.") < 0) {
        app.url = app.url.replace('.neworg.net', 'demo.neworg.net');
      }
      this.appSelected.next(app);
      return;
    }

    this.appNotFound.next(id);
  }

  /**
   * Get app url from app id
   * @param id
   * @returns App url
   */
  getAppUrl(id: string): string {
    let app = this._companyProperties.userCompanyApps.find(a => {
      return a.id.toLowerCase() == id.toLowerCase()
    })

    if (app !== undefined) {
      this.appSelected.next(app);

      return app.url;
    }

    return null;
  }

  //#region Style class

  /**
   * Set app Navbar class from app params
   * @param id
   */
  setAppNavbarClass(idApp: any) {
    this._debugLogger.logAction('AppStateHolder', 'setAppNavbarClass', { idApp });
    if (idApp === 'setHREmergencyNavbarClass') {
      this.setHREmergencyNavbarClass();
      return;
    } else {
      let app = this._companyProperties.userCompanyApps.find(a => a.id.toLowerCase() == idApp.toLowerCase());
      this._appProperty.setAppClass(app.cssParams.appClass);
      this._appProperty.setAppName('AmSuite ' + app.functionName);
    }
  }

  //TODO: TEMPORANEO
  setRefundedCostsNavbarClass() {
    this._appProperty.setAppClass('app-green');
    this._appProperty.setAppName('AmSuite Nota spese');
  }

  setHREmergencyNavbarClass() {
    this._appProperty.setAppClass('app-green');
    this._appProperty.setAppName('AmSuite Lista di emergenza');
  }

  /**
   * Get icon class for sidebar
   * @param icon
   * @returns class style for fontawesome or primeng icons
   */
  iconClass(icon: any) {
    if (icon.includes('fa')) {
      return 'fa'
    } else {
      return 'pi'
    }
  }

  //#endregion

  //#endregion


  isAuthenticated(): Observable<boolean> {
    const authUrl = this._appService.getServerBaseUrl + "/auth";
    return this.http.get(authUrl, { withCredentials: true }).pipe(
      map((response: Response) => {
        let isAuthenticated = response;

        if (isAuthenticated)
          return true;

        return false;
      }))
  }


  signOut(): void {
    this._debugLogger.logAction('AppStateHolder', 'signOut');
    const cookie = this.getCookie('authType');
    this.removeCookie('authType');
    this.removeCookie('authToken');
    this.removeCookie('authTypeDomain');
    this.removeCookie('ImpersonateState')

    this._appService.signOut().subscribe({
      next: response => {

        if (cookie === 'asp') {
          window.location.replace('/');
        }
        else if (cookie === 'b2c') {
          window.location.assign(response);
        }
      }
    });
  }

  //#region Crypting

  encryptValue(value: string): string {
    const key = CryptoJS.enc.Utf8.parse(environment.cryptingKey);
    const encrypted = CryptoJS.AES.encrypt(value, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString();
  }


  decryptValue(value: string): string {
    const key = CryptoJS.enc.Utf8.parse(environment.cryptingKey);
    const decrypted = CryptoJS.AES.decrypt(value, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  //#endregion

  //#region Cookies


  /**
* @param {string} key Id to use for lookup.
* @returns {string} Raw cookie value.
*/
  getCookie(key: string) {
    let cookie = this._cookieService.get(key);

    try {
      const decryptedValue = this.decryptValue(cookie);
      const result = decryptedValue && decryptedValue.trim().length > 0 ? decryptedValue : cookie;
      this._debugLogger.logAction('AppStateHolder', 'getCookie', { key, value: result });
      return result;

    } catch (e) {
      this._debugLogger.logAction('AppStateHolder', 'getCookie', { key, value: cookie });
      return cookie;
    }
  }

  /**
   * @param {string} key Id for the `value`.
   * @param {string} value Raw value to be stored.
   */
  setCookie(key: string, value: string): void {
    this._debugLogger.logAction('AppStateHolder', 'setCookie', { key, value });
    let domain = location.hostname.indexOf('localhost') >= 0 ? 'localhost' : '.neworg.net'
    let expiryDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365); // 1 year

    let opt: CookieOptions = {
      domain: domain,
      expires: expiryDate,
    };

    try {
      const encryptedValue = this.encryptValue(value);
      return this._cookieService.put(key, encryptedValue, opt);

    } catch (e) {
      return this._cookieService.put(key, value, opt);
    }
  }

  /**
  * @param {string} key Id of the key-value pair to delete.
  * @param {CookieOptions} options (Optional) Options object.
  */
  removeCookie(key: string): void {
    this._debugLogger.logAction('AppStateHolder', 'removeCookie', { key });
    let domain = location.hostname.indexOf('localhost') >= 0 ? 'localhost' : '.neworg.net'

    let opt: CookieOptions = {
      domain: domain
    };

    return this._cookieService.remove(key, opt);
  }


  //#endregion

  //#region JWT Token
  validateJWT(token: any): boolean {
    return this.isValidFormat(token) && this.isValidLength(token) && this.isValidContent(token);
  }


  isValidFormat(token: string): boolean {
    return token.split('.').length === 3;
  }


  isValidLength(token: string, minLength: number = 10): boolean {
    return token.split('.').every(part => part.length >= minLength);
  }

  isValidContent(token: string): boolean {
    try {
      const parts = token.split('.');
      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));
      // Qui puoi aggiungere ulteriori controlli sul contenuto del header e del payload se necessario
      return true;
    } catch (error) {
      return false; // Qualcosa non va nei dati decodificati
    }
  }

  //#endregion

  navigateToAppFromEvent(navigationParams: string) {
    this._debugLogger.logNavigation('AppStateHolder.navigateToAppFromEvent', { navigationParams });
    let params = JSON.parse(navigationParams);
    this.setAppNavbarClass(params.appId);
    let appUrl = this.getAppUrl(params.appId)
    let link = ['/app', `${appUrl}/${params.path}/${params.id}`];

    this._router.navigate(link);
  }

  clearToasts() {
    this._debugLogger.logAction('AppStateHolder', 'clearToasts');
    this._messageService.clear();
  }

  mapDevURL(companies: any) {
    if (this._document.location.host.indexOf("-dev") >= 0) {
      companies.forEach(company => {
        company.apps.forEach(app => {
          if (app.url.includes('amsuite.neworg.net'))
            app.url = app.url.replace('amsuite.neworg.net', 'amsuite-dev.neworg.net')

          else if (app.url.includes('crm.neworg.net'))
            app.url = app.url.replace('crm.neworg.net', 'amcrm-dev.neworg.net')

          else if (app.url.includes('calendar.neworg.net'))
            app.url = app.url.replace('calendar.neworg.net', 'calendar-dev.neworg.net')

          else if (app.url.includes('reportbi.neworg.net'))
            app.url = app.url.replace('reportbi.neworg.net', 'reportbi-dev.neworg.net')

          else if (app.url.includes('dialog.neworg.net'))
            app.url = app.url.replace('dialog.neworg.net', 'dialog-dev.neworg.net')

          else if (app.url.includes('fastpublisher.neworg.net'))
            app.url = app.url.replace('fastpublisher.neworg.net', 'fastpublisher-dev.neworg.net')

          else if (app.url.includes('suiteautomation.neworg.net'))
            app.url = app.url.replace('suiteautomation.neworg.net', 'suiteautomation-dev.neworg.net')

          else if (app.url.includes('attendancesheet.neworg.net'))
            app.url = app.url.replace('attendancesheet.neworg.net', 'attendancesheet-dev.neworg.net')

          else if (app.url.includes('partysharedarchive.neworg.net'))
            app.url = app.url.replace('partysharedarchive.neworg.net', 'partysharedarchive-dev.neworg.net')

          else if (app.url.includes('billing.neworg.net'))
            app.url = app.url.replace('billing.neworg.net', 'billing-dev.neworg.net')

          else if (app.url.includes('https://refundedcosts.neworg.net/'))
            app.url = app.url.replace('https://refundedcosts.neworg.net/', 'https://refundedcosts-dev.neworg.net/')
        });
      });
    }
  }
}
